/**
 * @fileoverview Tipos TypeScript para integración Catastro en PTEL Tool
 * @module types/catastro
 * @version 2.0.0
 * @date 2026-05-10 (S4-FUEL-DEPRECATE cleanup)
 *
 * Contenido tras la deprecación de `FuelCatastroGeocoder`:
 * - Provincias de Andalucía (8 códigos `as const`).
 * - Niveles de riesgo industrial y mapa `DESTINO_NIVEL_RIESGO`, input vivo de
 *   `services/cotejo/CriticidadNormativa.ts` (OL-02).
 * - `CatastroInmueble`, `USO_DESCRIPTIONS`, `DESTINO_DESCRIPTIONS`: tipos
 *   compartidos con el pipeline AWS Lambda Catastro (`lambda/parsers/catParser.ts`,
 *   `lambda/handlers/syncCatastroProvincia.ts`) para parsear ficheros CAT y
 *   sincronizar parcelas a R2.
 *
 * Símbolos eliminados en S4 (10-may-2026): `DESTINOS_ALTO/MEDIO/BAJO_RIESGO`,
 * `DESTINOS_INDUSTRIALES_PTEL`, `DestinoIndustrialPTEL`, `DESTINO_INDUSTRIAL_DESCRIPTIONS`
 * (alias deprecated), `USOS_RIESGO_INDUSTRIAL`, `UsoRiesgoIndustrial`. Todos con
 * 0 consumers verificados en TODO el repo (src/ + lambda/), ver
 * `.ptel/AUDIT_CATASTRO_MAPEO_20260510.md` §3.4 + arranque S4 §2.5.
 *
 * Símbolo eliminado en TAU1 (3-ago-2026): `tipologiaAUsosPTEL` (adaptador TUN3),
 * con 0 consumidores productivos verificados en `src/` + `lambda/` — sus únicas
 * llamadas vivían en su propio fichero de pruebas. Arrastraba además la huella
 * `G` para POLICIAL/BOMBEROS/EMERGENCIAS/VIAL/PUERTO que `UCC1` midió falsa
 * contra la DGC (`G` = Turismo/Hostelería, ver `USO_DESCRIPTIONS` en este mismo
 * fichero). Ver `V-041` en `.ptel/VIGILANCIA_CORRIDA.md`.
 */

// =============================================================================
// CONSTANTES
// =============================================================================

/** Códigos de uso catastral relevantes para infraestructuras PTEL */
export const USOS_PTEL = ['Y', 'E', 'P', 'K', 'T', 'R', 'G', 'I'] as const;

/** Tipo derivado de códigos de uso PTEL */
export type UsoPTEL = (typeof USOS_PTEL)[number];

/** Códigos de provincia de Andalucía */
export const PROVINCIAS_ANDALUCIA = ['04', '11', '14', '18', '21', '23', '29', '41'] as const;

/** Tipo derivado de códigos de provincia */
export type ProvinciaAndalucia = (typeof PROVINCIAS_ANDALUCIA)[number];

/**
 * Mapeo código uso → descripción.
 *
 * Consumido por `lambda/parsers/catParser.ts` para resolver la descripción
 * humana del campo `usoDesc` en `CatastroInmueble`.
 */
export const USO_DESCRIPTIONS: Record<UsoPTEL, string> = {
  Y: 'Sanidad/Beneficencia',
  E: 'Cultural',
  P: 'Edificio Público',
  K: 'Deportivo',
  T: 'Espectáculos',
  R: 'Religioso',
  G: 'Turismo/Hostelería',
  I: 'Industrial',
};

// =============================================================================
// CLASIFICACIÓN INDUSTRIAL - PUNTOS DE RIESGO PTEL
// =============================================================================

/** Niveles de riesgo industrial para PTEL */
export type NivelRiesgoIndustrial = 'ALTO' | 'MEDIO' | 'BAJO';

/**
 * Mapeo destino industrial → nivel de riesgo.
 *
 * Consumido por `services/cotejo/CriticidadNormativa.ts` para resolver el
 * nivel de riesgo industrial OL-02 a partir del código `destino_cod` Catastro.
 */
export const DESTINO_NIVEL_RIESGO: Record<string, NivelRiesgoIndustrial> = {
  // Alto riesgo - Prioridad máxima en PTEL
  IQU: 'ALTO',  // Industria química (SEVESO, mercancías peligrosas)
  IEN: 'ALTO',  // Industria energética (centrales, subestaciones)
  IPE: 'ALTO',  // Industria pesada
  IEX: 'ALTO',  // Industria extractiva (canteras, minas)
  
  // Medio riesgo
  IFB: 'MEDIO', // Fábrica genérica
  IAG: 'MEDIO', // Industria agroalimentaria (cámaras frío, silos)
  IGR: 'MEDIO', // Grandes superficies industriales (polígonos)
  JAG: 'MEDIO', // Industria agraria (bodegas, almazaras)
  VAL: 'MEDIO', // Almacén agrario (silos, grano)
  
  // Bajo riesgo
  INA: 'BAJO',  // Nave industrial genérica
  IAL: 'BAJO',  // Almacén
  ITA: 'BAJO',  // Taller
  ICO: 'BAJO',  // Industria construcción
};

/**
 * Descripción detallada de destinos catastrales (todos los usos PTEL).
 *
 * Consumido por `lambda/parsers/catParser.ts` para resolver el campo `destinoDesc`
 * de `CatastroInmueble` a partir del código `destino_cod` Catastro.
 */
export const DESTINO_DESCRIPTIONS: Record<string, string> = {
  // Uso Y - Sanidad/Beneficencia
  YSA: 'Centro sanitario / Hospital',
  YHO: 'Residencia de ancianos',
  YSB: 'Centro asistencial / Beneficencia',
  YAM: 'Ambulatorio / Centro de salud',
  YCL: 'Clínica privada',

  // Uso E - Cultural/Educativo
  EBI: 'Biblioteca',
  ECU: 'Centro cultural',
  EIN: 'Instituto de enseñanza',
  ECO: 'Colegio',
  EUN: 'Universidad / Facultad',
  EMU: 'Museo',
  EGU: 'Guardería / Escuela infantil',

  // Uso P - Edificio Público
  PAD: 'Edificio administrativo',
  PAY: 'Ayuntamiento / Casa consistorial',
  PCO: 'Oficina de correos',
  PJU: 'Juzgados',
  PCA: 'Cuartel / Comisaría',
  PBO: 'Parque de bomberos',
  PGC: 'Cuartel Guardia Civil',

  // Uso K - Deportivo
  KPD: 'Polideportivo',
  KGI: 'Gimnasio',
  KPI: 'Piscina cubierta',
  KCA: 'Campo de fútbol / Estadio',
  KFR: 'Frontón',

  // Uso T - Espectáculos
  TTE: 'Teatro',
  TCI: 'Cine',
  TST: 'Sala de fiestas / Discoteca',
  TPA: 'Pabellón de exposiciones',
  TPT: 'Plaza de toros',

  // Uso R - Religioso
  RIG: 'Iglesia',
  RCA: 'Capilla / Ermita',
  RMO: 'Monasterio / Convento',
  RCE: 'Cementerio / Tanatorio',

  // Uso G - Turismo/Hostelería
  GHO: 'Hotel',
  GPA: 'Parador / Hostal',
  GRE: 'Restaurante',
  GCA: 'Cafetería / Bar',
  GCM: 'Camping',
  GAP: 'Apartamento turístico',

  // Uso I - Industrial
  IQU: 'Industria química',
  IEN: 'Industria energética',
  IPE: 'Industria pesada',
  IEX: 'Industria extractiva',
  IFB: 'Fábrica',
  IAG: 'Industria agroalimentaria',
  IGR: 'Gran superficie industrial',
  INA: 'Nave industrial',
  IAL: 'Almacén industrial',
  ITA: 'Taller',
  ICO: 'Industria construcción',

  // Uso J - Industria agraria
  JAG: 'Industria agraria (bodega/almazara)',

  // Uso V - Almacén agrario
  VAL: 'Almacén agrario (silo/grano)',

  // Uso Z - Ganadería intensiva
  ZGA: 'Explotación ganadera intensiva',
};

// =============================================================================
// INTERFACES PRINCIPALES
// =============================================================================

/**
 * Inmueble catastral con datos relevantes para PTEL.
 *
 * Consumido por `lambda/parsers/catParser.ts` y `lambda/handlers/syncCatastroProvincia.ts`
 * para tipar los registros extraídos de los ficheros CAT (Types 11, 14, 15) en el
 * pipeline de sincronización Catastro Lambda → R2.
 */
export interface CatastroInmueble {
  /** Referencia catastral completa (20 caracteres) */
  refcat: string;

  /** Código INE del municipio (5 dígitos) */
  codINE: string;

  /** Coordenada X UTM30 ETRS89 */
  x: number;

  /** Coordenada Y UTM30 ETRS89 */
  y: number;

  /** Sistema de referencia (siempre EPSG:25830) */
  srs: 'EPSG:25830';

  /** Código de uso principal (Y, E, P, K, T, R, G, I) */
  uso: UsoPTEL;

  /** Descripción del uso */
  usoDesc: string;

  /** Código de destino específico (3 letras, ej: YSA, EBI, IQU) */
  destino?: string;

  /** Descripción del destino */
  destinoDesc?: string;

  /** Nivel de riesgo industrial (solo para usos I, J, V, Z) */
  nivelRiesgo?: NivelRiesgoIndustrial;

  /** Dirección normalizada */
  direccion: string;

  /** Código postal */
  cp: string;

  /** Superficie construida en m² */
  superficie?: number;

  /** Año de construcción */
  anoConstruccion?: number;
}

